import React from "react";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import "./IntroOfferStatusCard.css";

// frontend/src/components/staff/IntroOfferStatusCard.js
//
// INTRO-OFFER-001-E — admin Customer Detail intro-offer status card.
// Renders between the ESIGN status card and the tabs on /staff/customers/:id
// per Zuri visual spec §4.1.
//
// Spec sources (locked):
//   docs/design/INTRO-OFFER-001-design.md (Zuri)
//     §4    Surface 2 — Customer Detail page admin indicator
//     §4.1  Placement (between ESIGN status card and tabs)
//     §4.2  Visual states (5 lifecycle + 1 legacy)
//     §4.3  Loading + error states
//     §4.4  What the card does NOT show (no claim-on-behalf / extend / refund)
//     §4.5  Accessibility (aria-label, color-never-sole-differentiator,
//           keyboard navigation)
//     §10.2 Visual Definition of Done
//   docs/architecture/INTRO-OFFER-001-tech.md (Marcus)
//     §"GET /api/admin/customers/:id" — additive `introOffer` block including
//     adminHistory, expirationReason, pendingBookingId, cancelledBookingCount,
//     and admin-only adminHistory derived from AuditLog
//   docs/backlog/INTRO-OFFER-001-E-admin-customer-detail-indicator.md
//     Scenarios 1-14 + DoD
//
// Companion (visual-rhythm reference): EsignStatusCard — same chrome, same
// row pattern, same uppercase label convention. Reused per spec §4.1.
//
// Data source — backward-compatibility posture:
//   The card consumes the additive `introOffer` block on the admin endpoint
//   response (the same shape Marcus's tech doc specifies for the admin
//   endpoint; pure-additive on `/api/staff/customers/:id`). At first-landing
//   the BE may not yet serialize the enriched server-side fields
//   (`effectiveStatus`, `pendingBookingId`, `pendingBookingStartTime`,
//   `cancelledBookingCount`, `adminHistory`, `expirationReason`). The card
//   reads them when present and falls back to deriving the displayable
//   state from the raw `introOffer` sub-doc shape stored on User
//   (`status`, `eligibleAt`, `claimWindowEndsAt`, `claimedAt`, `expiresAt`,
//   `usedAt`, `usedOnBookingId`). This keeps the FE shippable independent
//   of a BE follow-up that adds `withIntroOfferStatus()` to the staff
//   detail endpoint.
//
// Production-safety:
//   - Pure-presentational; no API calls.
//   - Graceful fallback: renders nothing if BOTH `introOffer` and
//     `hasFreeFlexible` are absent / falsy (Scenario 12 — page does not
//     shift layout, surrounding card stack renders unaffected).
//   - Legacy detection: `introOffer === undefined/null && hasFreeFlexible
//     === true` => render state 4.2.6 legacy.
//   - No CTAs that intervene through the card (no claim-on-behalf, no
//     extend, no refund) — spec §4.4. The only interactive element is the
//     inline "view booking" Link in state 4.2.2b.

const PHOENIX_TZ = "America/Phoenix";

/**
 * Phoenix-MST timestamp formatter — defensive client-side rendering for
 * the admin card. Admin spec §4 uses an explicit row format (label-value
 * pair) rather than verbatim `displayCopy.primary`, so we always format
 * timestamps locally with explicit "MST" suffix per CLAUDE.md.
 *
 * @param {string} iso ISO 8601 timestamp
 * @returns {string} e.g., "Apr 19, 2026 14:32 MST"
 */
const formatTimestamp = (iso) => {
  if (!iso) return "";
  const m = moment.tz(iso, PHOENIX_TZ);
  if (!m.isValid()) return "";
  return `${m.format("MMM D, YYYY HH:mm")} MST`;
};

/**
 * Compute days-remaining vs a deadline (rounded up so "1 day to claim"
 * covers the final 24h window per Story D parity §3.2.1b). Negative result
 * means already past — caller should treat as expired regardless.
 *
 * @param {string|Date} deadlineIso
 * @param {Date} [now]
 * @returns {number}
 */
const daysRemaining = (deadlineIso, now) => {
  if (!deadlineIso) return 0;
  const deadline = new Date(deadlineIso).getTime();
  const t = (now || new Date()).getTime();
  if (Number.isNaN(deadline)) return 0;
  return Math.max(0, Math.ceil((deadline - t) / (24 * 60 * 60 * 1000)));
};

/**
 * Derive the effective lifecycle status from a raw `introOffer` sub-doc.
 * Mirrors the BE `effectiveIntroOfferStatus()` lazy-fallback interpreter
 * for the cron-lag edge case (read at 00:01 Phoenix-MST after midnight
 * cliff but before the 00:05 cron sweep — read path catches the
 * transition). Used only when the BE has NOT yet provided
 * `effectiveStatus` on the admin endpoint response (defensive fallback).
 *
 * @param {object} introOffer  raw sub-doc shape from User.introOffer
 * @param {Date} [now]
 * @returns {string} one of the 5 lifecycle states
 */
const deriveEffectiveStatus = (introOffer, now) => {
  if (!introOffer) return null;
  const t = (now || new Date()).getTime();
  const claimEnd = introOffer.claimWindowEndsAt
    ? new Date(introOffer.claimWindowEndsAt).getTime()
    : null;
  const useEnd = introOffer.expiresAt
    ? new Date(introOffer.expiresAt).getTime()
    : null;

  if (introOffer.status === "eligible" && claimEnd && t >= claimEnd) {
    return "expired-unclaimed";
  }
  if (introOffer.status === "claimed" && useEnd && t >= useEnd) {
    return "expired-unused";
  }
  return introOffer.status;
};

// -------------------------------------------------------------------------
// Icons (16pt). All decorative — adjacent text labels carry meaning per
// spec §4.5 ("icons are decorative; row labels carry the meaning"). Mirrors
// EsignStatusIcon vocabulary precisely so the visual rhythm of the staff
// surface is consistent.
// -------------------------------------------------------------------------

const GiftIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <rect x="2" y="6.5" width="12" height="7" stroke="currentColor" strokeWidth="1.2" fill="none" />
    <rect x="1" y="4.5" width="14" height="2.5" stroke="currentColor" strokeWidth="1.2" fill="currentColor" />
    <path d="M8 4.5V14" stroke="#ffffff" strokeWidth="1" />
    <path
      d="M8 4.5C8 4.5 6.5 2 5 2C4 2 3.5 2.7 3.5 3.5C3.5 4.3 4.2 4.5 5 4.5"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    <path
      d="M8 4.5C8 4.5 9.5 2 11 2C12 2 12.5 2.7 12.5 3.5C12.5 4.3 11.8 4.5 11 4.5"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="7" fill="currentColor" />
    <path
      d="M5 8l2.2 2.2L11 6"
      stroke="#ffffff"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

// Calendar icon — for the optional state-4.2.2b pending-booking row icon.
// Currently the spec puts the calendar emphasis on the customer side (§3.2.3);
// the admin side keeps the green-check Status icon but ADDS a fourth
// "Pending booking" row. We surface a small calendar glyph as a row-prefix
// to visually distinguish that row (still decorative; row label is the
// equivalent text).
const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <rect
      x="2"
      y="3.5"
      width="12"
      height="11"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.2"
      fill="none"
    />
    <path d="M2 6.5h12" stroke="currentColor" strokeWidth="1.2" />
    <path d="M5.5 2v3M10.5 2v3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const XOutlineIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5.5 5.5l5 5M10.5 5.5l-5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const XFilledIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="7" fill="currentColor" />
    <path
      d="M5.5 5.5l5 5M10.5 5.5l-5 5"
      stroke="#ffffff"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M8 7v4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="8" cy="4.8" r="0.9" fill="currentColor" />
  </svg>
);

// -------------------------------------------------------------------------
// Row primitives
// -------------------------------------------------------------------------

/**
 * One generic row of the card: label-column (~96px) + value-column.
 * Optionally prefixes the value with an inline icon (e.g., the Status row).
 */
const Row = ({ label, value, icon, iconClass, valueClassName, children }) => (
  <div className="intro-offer-status-card__row">
    <span className="intro-offer-status-card__row-label">{label}</span>
    {icon && (
      <span
        className={`intro-offer-status-card__icon ${iconClass || ""}`}
        aria-hidden="true"
      >
        {icon}
      </span>
    )}
    <span
      className={
        valueClassName
          ? `intro-offer-status-card__row-value ${valueClassName}`
          : "intro-offer-status-card__row-value"
      }
    >
      {children || value}
    </span>
  </div>
);

// -------------------------------------------------------------------------
// State renderers — one per §4.2 sub-state. Each returns the JSX body
// (rows) for the card. The wrapper renders the section + label.
// -------------------------------------------------------------------------

const EligibleRows = ({ introOffer }) => {
  const days = daysRemaining(introOffer.claimWindowEndsAt);
  const dayLabel = days === 1 ? "day" : "days";
  return (
    <>
      <Row
        label="Status"
        icon={<GiftIcon />}
        iconClass="intro-offer-status-card__icon--eligible"
      >
        {`Eligible — ${days} ${dayLabel} to claim`}
      </Row>
      <Row
        label="Eligible from"
        value={formatTimestamp(introOffer.eligibleAt)}
      />
      <Row label="Claim by" value={formatTimestamp(introOffer.claimWindowEndsAt)} />
    </>
  );
};

const ClaimedRows = ({ introOffer }) => {
  const days = daysRemaining(introOffer.expiresAt);
  const dayLabel = days === 1 ? "day" : "days";
  const hasPendingBooking = Boolean(introOffer.pendingBookingId);

  return (
    <>
      <Row
        label="Status"
        icon={<CheckIcon />}
        iconClass="intro-offer-status-card__icon--success"
      >
        {`Claimed — ${days} ${dayLabel} to use`}
      </Row>
      <Row label="Claimed" value={formatTimestamp(introOffer.claimedAt)} />
      <Row label="Use deadline" value={formatTimestamp(introOffer.expiresAt)} />
      {hasPendingBooking && (
        <div className="intro-offer-status-card__row">
          <span className="intro-offer-status-card__row-label">
            Pending booking
          </span>
          <span
            className="intro-offer-status-card__icon intro-offer-status-card__icon--pending"
            aria-hidden="true"
          >
            <CalendarIcon />
          </span>
          <span className="intro-offer-status-card__row-value">
            {formatTimestamp(introOffer.pendingBookingStartTime)}
          </span>
          <Link
            to={`/staff/edit-booking/${introOffer.pendingBookingId}`}
            className="intro-offer-status-card__view-booking-link"
            aria-label="View pending intro-offer booking"
          >
            view booking
          </Link>
        </div>
      )}
    </>
  );
};

const UsedRows = ({ introOffer }) => (
  <>
    <Row
      label="Status"
      icon={<CheckIcon />}
      iconClass="intro-offer-status-card__icon--success"
    >
      Used
    </Row>
    <Row label="Claimed" value={formatTimestamp(introOffer.claimedAt)} />
    <Row label="Used" value={formatTimestamp(introOffer.usedAt)} />
  </>
);

const ExpiredUnclaimedRows = ({ introOffer }) => (
  <>
    <Row
      label="Status"
      icon={<XOutlineIcon />}
      iconClass="intro-offer-status-card__icon--muted"
    >
      Expired — not claimed
    </Row>
    <Row
      label="Eligible from"
      value={formatTimestamp(introOffer.eligibleAt)}
    />
    <Row
      label="Expired"
      value={formatTimestamp(introOffer.claimWindowEndsAt)}
    />
  </>
);

const ExpiredUnusedRows = ({ introOffer }) => {
  const cancelledCount =
    typeof introOffer.cancelledBookingCount === "number"
      ? introOffer.cancelledBookingCount
      : null;
  return (
    <>
      <Row
        label="Status"
        icon={<XFilledIcon />}
        iconClass="intro-offer-status-card__icon--violation"
      >
        Expired — not used
      </Row>
      <Row label="Claimed" value={formatTimestamp(introOffer.claimedAt)} />
      <Row label="Expired" value={formatTimestamp(introOffer.expiresAt)} />
      {cancelledCount !== null && cancelledCount > 0 && (
        <Row
          label="Cancelled bookings"
          value={String(cancelledCount)}
        />
      )}
    </>
  );
};

const LegacyRows = () => (
  <>
    <Row
      label="Status"
      icon={<InfoIcon />}
      iconClass="intro-offer-status-card__icon--muted"
    >
      Legacy free-flexible
    </Row>
    <p className="intro-offer-status-card__legacy-paragraph">
      This customer registered before intro offer tracking was introduced and
      holds the legacy free-flexible entitlement. No two-stage clock applies.
    </p>
  </>
);

// -------------------------------------------------------------------------
// Top-level component
// -------------------------------------------------------------------------

/**
 * Admin Customer Detail intro-offer status card. Renders one of 6 visual
 * states per Zuri spec §4.2 (5 lifecycle + 1 legacy).
 *
 * @param {object} props
 * @param {object} [props.introOffer]
 *   The `introOffer` block from `/api/staff/customers/:customerId`. May be
 *   the raw stored sub-doc (status / eligibleAt / claimWindowEndsAt /
 *   claimedAt / expiresAt / usedAt / usedOnBookingId) OR a server-enriched
 *   shape including `effectiveStatus` / `pendingBookingId` /
 *   `pendingBookingStartTime` / `cancelledBookingCount`. The card prefers
 *   server-enriched fields when present and falls back to deriving the
 *   displayable state from the raw shape (see deriveEffectiveStatus).
 *   Null / undefined => check legacy fallback via `hasFreeFlexible`.
 * @param {boolean} [props.hasFreeFlexible]
 *   Legacy free-flexible flag on the customer (top-level User field). When
 *   `introOffer` is absent AND this is true, the card renders the legacy
 *   state §4.2.6. Default-false posture means new customers without a
 *   well-formed `introOffer` simply do not render the card.
 * @param {boolean} [props.loading=false]
 *   While true, render the skeleton state per §4.3 (no layout shift on
 *   resolve).
 * @param {boolean} [props.error=false]
 *   Render the error placeholder state per §4.3 ("—" rows + "Couldn't
 *   load. Refresh to try again." helper).
 */
const IntroOfferStatusCard = ({
  introOffer,
  hasFreeFlexible,
  loading = false,
  error = false,
}) => {
  // Loading skeleton — matches §4.3. Reserves card-height so the page does
  // not shift on resolve.
  if (loading) {
    return (
      <section
        className="intro-offer-status-card intro-offer-status-card--loading"
        aria-label="Intro offer compliance status for this customer"
        aria-busy="true"
        role="status"
      >
        <div className="intro-offer-status-card__label-skeleton" />
        <div className="intro-offer-status-card__row-skeleton" />
        <div className="intro-offer-status-card__row-skeleton" />
        <div className="intro-offer-status-card__row-skeleton" />
      </section>
    );
  }

  // Error placeholder — matches §4.3. Same chrome as the resolved card,
  // with "—" in every row and a helper line. The rest of the Customer
  // Detail page renders normally (Scenario 12).
  if (error) {
    return (
      <section
        className="intro-offer-status-card intro-offer-status-card--error"
        aria-label="Intro offer compliance status for this customer"
      >
        <span className="intro-offer-status-card__label">INTRO OFFER</span>
        <Row label="Status" value="—" />
        <Row label="Eligible from" value="—" />
        <Row label="Claim by" value="—" />
        <p className="intro-offer-status-card__error-helper">
          Couldn't load. Refresh to try again.
        </p>
      </section>
    );
  }

  // Legacy fallback per Scenario 7 (state 4.2.6).
  const isLegacy = !introOffer && Boolean(hasFreeFlexible);

  // Graceful absence per Scenario 12 + story Notes — render NOTHING when
  // both signals are absent. The card is purely additive; the surrounding
  // Customer Detail page stack continues to render unaffected.
  if (!introOffer && !isLegacy) {
    return null;
  }

  const status = isLegacy
    ? "legacy"
    : introOffer.effectiveStatus || deriveEffectiveStatus(introOffer);

  let stateClass = "intro-offer-status-card--unknown";
  let body = null;

  if (status === "eligible") {
    stateClass = "intro-offer-status-card--eligible";
    body = <EligibleRows introOffer={introOffer} />;
  } else if (status === "claimed") {
    stateClass = "intro-offer-status-card--claimed";
    body = <ClaimedRows introOffer={introOffer} />;
  } else if (status === "used") {
    stateClass = "intro-offer-status-card--used";
    body = <UsedRows introOffer={introOffer} />;
  } else if (status === "expired-unclaimed") {
    stateClass = "intro-offer-status-card--expired-unclaimed";
    body = <ExpiredUnclaimedRows introOffer={introOffer} />;
  } else if (status === "expired-unused") {
    stateClass = "intro-offer-status-card--expired-unused";
    body = <ExpiredUnusedRows introOffer={introOffer} />;
  } else if (status === "legacy") {
    stateClass = "intro-offer-status-card--legacy";
    body = <LegacyRows />;
  } else {
    // Unknown / malformed status — render nothing rather than crash the
    // page (defense-in-depth; the rest of CustomerProfile is unaffected).
    return null;
  }

  return (
    <section
      className={`intro-offer-status-card ${stateClass}`}
      aria-label="Intro offer compliance status for this customer"
      data-testid="intro-offer-status-card"
    >
      <span className="intro-offer-status-card__label">INTRO OFFER</span>
      {body}
    </section>
  );
};

export default IntroOfferStatusCard;
export {
  // Exported for unit-test access without going through the full top-level
  // wrapper.
  formatTimestamp,
  daysRemaining,
  deriveEffectiveStatus,
};
